/**
 * Hotel package / profile selling-price helpers (Gemonix packages, client profile, navigation seed).
 * Agent hotel **search results** list/map: use `agentHotelSearchLowestPackagePrice` from
 * `agentHotelSearchApiPrice.js` — raw API only (no pipeline here).
 */

import {
  formatHotelSearchAmountForDisplay,
  isKnownHotelSearchCurrencyCode,
  normalizeHotelSearchCurrencyCode,
} from "../constants/hotelCurrencies";
import { isHotelProfilePriceAuditEnabled } from "./hotelSearchDebugFlags";
import {
  applyFXSettlement,
  roundMoney2,
  runHotelSellingDisplayFromEngine,
} from "../pricing/pricingEngine";
import { compactPricingAuditMeta } from "../pricing/compactPricingAuditMeta.js";
import {
  PRICING_AUDIT_PRODUCT_TYPE,
  normalizePricingAuditProductType,
} from "../pricing/pricingAuditProductType.js";
import { recordPricingAudit } from "../pricing/pricingEngineAuditStore.js";

export { formatHotelSearchAmountForDisplay } from "../constants/hotelCurrencies";
import { agentHotelSearchLowestPackagePrice } from "./agentHotelSearchApiPrice";
import { formatHotelIlsRoundedDisplay } from "./formatHotelIlsRoundedDisplay";

/**
 * Supplier USD for one hotelInformation package row (Gemonix).
 * SoT: PackagePrice.FinalPrice → SimplePrice → Rooms[0].Price.FinalPrice → legacy finalPrice.
 * @param {object|null|undefined} pkg
 * @returns {number|null} positive USD or null if none
 */
export function getHotelProfilePackageSupplierUsd(pkg) {
  if (!pkg || typeof pkg !== "object") return null;
  const fromPackagePrice = Number(
    pkg.PackagePrice?.FinalPrice ??
      pkg.PackagePrice?.finalPrice ??
      pkg.packagePrice?.FinalPrice ??
      pkg.packagePrice?.finalPrice
  );
  if (Number.isFinite(fromPackagePrice) && fromPackagePrice > 0) {
    return fromPackagePrice;
  }
  const simple = Number(pkg.SimplePrice ?? pkg.simplePrice);
  if (Number.isFinite(simple) && simple > 0) {
    return simple;
  }
  const rooms = pkg.Rooms;
  const roomsArr = Array.isArray(rooms)
    ? rooms
    : rooms && typeof rooms === "object"
      ? Object.values(rooms)
      : [];
  const r0 = roomsArr[0];
  const roomFinal = Number(r0?.Price?.FinalPrice ?? r0?.price?.FinalPrice);
  if (Number.isFinite(roomFinal) && roomFinal > 0) {
    return roomFinal;
  }
  const legacy = Number(pkg.finalPrice ?? pkg.FinalPrice);
  if (Number.isFinite(legacy) && legacy > 0) {
    return legacy;
  }
  return null;
}

export const HOTEL_PRICE_SOURCE = {
  MIN_PACKAGE_FINAL_PRICE_USD: "min_package_final_price_usd",
  PACKAGE_FINAL_PRICE_USD: "package_final_price_usd",
  STARTING_PRICE_FALLBACK_USD: "starting_price_fallback_usd",
  /** hotelInformation StartingPrice — profile hero only (not per-room). */
  PROFILE_LOADED_STARTING_PRICE_USD: "profile_loaded_starting_price_usd",
  COMPOSITE_VALIDATED_TOTAL_USD: "composite_validated_total_usd",
  NAV_SEED_USD: "nav_seed_usd",
};

/**
 * @param {Array<{ packages?: Array<object> }>|null|undefined} boxes
 * @returns {number|null} minimum positive supplier USD (via getHotelProfilePackageSupplierUsd) across packages
 */
export function minFinalPriceUsdFromHotelBoxes(boxes) {
  if (!Array.isArray(boxes)) return null;
  let min = null;
  for (const box of boxes) {
    const pkgs = box?.packages;
    if (!Array.isArray(pkgs)) continue;
    for (const pkg of pkgs) {
      const v = getHotelProfilePackageSupplierUsd(pkg);
      if (v != null) {
        min = min == null ? v : Math.min(min, v);
      }
    }
  }
  return min;
}

export function pickProfileLoadedHeroUsd(hotelInfo) {
  const sp = Number(hotelInfo?.StartingPrice);
  if (Number.isFinite(sp) && sp > 0) {
    return {
      supplierUsd: sp,
      priceSource: HOTEL_PRICE_SOURCE.PROFILE_LOADED_STARTING_PRICE_USD,
      fallbackUsed: false,
    };
  }
  const min = minFinalPriceUsdFromHotelBoxes(hotelInfo?.boxes);
  if (min != null) {
    return {
      supplierUsd: min,
      priceSource: HOTEL_PRICE_SOURCE.MIN_PACKAGE_FINAL_PRICE_USD,
      fallbackUsed: true,
    };
  }
  return { supplierUsd: null, priceSource: null, fallbackUsed: false };
}

export function pickProfileShellSupplierUsd({ compositePricingMeta, seed }) {
  const v = compositePricingMeta?.validated_total_price;
  if (v != null && Number.isFinite(Number(v)) && Number(v) > 0) {
    return {
      supplierUsd: Number(v),
      priceSource: HOTEL_PRICE_SOURCE.COMPOSITE_VALIDATED_TOTAL_USD,
      fallbackUsed: true,
    };
  }
  const fp = seed?.fromPrice;
  if (fp != null && Number.isFinite(Number(fp)) && Number(fp) > 0) {
    return {
      supplierUsd: Number(fp),
      priceSource: HOTEL_PRICE_SOURCE.NAV_SEED_USD,
      fallbackUsed: true,
    };
  }
  return { supplierUsd: null, priceSource: null, fallbackUsed: false };
}

/**
 * מטבע גולמי ל-Hero אחרי טעינת hotelInformation (StartingPrice או מינימום חבילות).
 * StartingPrice בקוד מסומן כ-USD; לא משתמשים ב-Currency כללי של המלון כ-raw עבורו (עלול להיות ILS לתצוגה בלבד).
 * במינימום חבילות: מטבע מהחבילה התואמת → Currency על המלון → USD.
 * @param {object|null|undefined} hotelInfo
 * @param {{ supplierUsd: number|null, priceSource?: string|null }} heroPick
 * @returns {string|null}
 */
export function pickProfileLoadedHeroTransactionCurrency(hotelInfo, heroPick) {
  if (!hotelInfo || heroPick?.supplierUsd == null) return null;
  if (heroPick.priceSource === HOTEL_PRICE_SOURCE.PROFILE_LOADED_STARTING_PRICE_USD) {
    return "USD";
  }
  if (heroPick.priceSource === HOTEL_PRICE_SOURCE.MIN_PACKAGE_FINAL_PRICE_USD) {
    const target = Number(heroPick.supplierUsd);
    const boxes = hotelInfo?.boxes;
    if (Array.isArray(boxes)) {
      for (const box of boxes) {
        const pkgs = box?.packages;
        if (!Array.isArray(pkgs)) continue;
        for (const pkg of pkgs) {
          const v = getHotelProfilePackageSupplierUsd(pkg);
          if (v != null && Math.abs(Number(v) - target) < 1e-4) {
            const c = getHotelProfilePackageCurrency(pkg);
            if (c) return c;
          }
        }
      }
    }
    return getHotelProfileHeroDisplayCurrency(hotelInfo) ?? "USD";
  }
  return getHotelProfileHeroDisplayCurrency(hotelInfo) ?? "USD";
}

/**
 * מטבע עסקה ל-shell לפני hotelInformation (composite validate / seed ניווט).
 */
export function pickProfileShellTransactionCurrency({
  compositePricingMeta,
  seed,
  profilePriceContext,
}) {
  const fromMeta =
    compositePricingMeta?.currency ??
    compositePricingMeta?.validated_currency ??
    compositePricingMeta?.price_currency;
  if (fromMeta != null && String(fromMeta).trim() !== "") {
    const u = String(fromMeta).trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(u)) return u;
  }
  const seedCur = seed?.currency ?? seed?.fromCurrency ?? seed?.priceCurrency;
  if (seedCur != null && String(seedCur).trim() !== "") {
    const u = String(seedCur).trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(u)) return u;
  }
  const fromSearch =
    profilePriceContext?.hotelSearchListDisplayCurrency ??
    profilePriceContext?.searchPhrase?.currency ??
    profilePriceContext?.selectedHotelCurrency;
  if (fromSearch != null && String(fromSearch).trim() !== "") {
    const u = String(fromSearch).trim().toUpperCase();
    if (isKnownHotelSearchCurrencyCode(u)) return u;
  }
  return resolveHotelProfileUiDisplayCurrency(profilePriceContext);
}

const ISO_CURRENCY_RE = /^[A-Z]{3}$/;

/**
 * ISO 4217 currency from package row when the API sends it (not guessed).
 * @param {object|null|undefined} pkg
 * @returns {string|null}
 */
export function getHotelProfilePackageCurrency(pkg) {
  if (!pkg || typeof pkg !== "object") return null;
  const candidates = [
    pkg.PackagePrice?.Currency,
    pkg.PackagePrice?.currency,
    pkg.packagePrice?.Currency,
    pkg.packagePrice?.currency,
    pkg.Currency,
    pkg.currency,
    pkg.PriceCurrency,
    pkg.priceCurrency,
  ];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    const u = String(c).trim().toUpperCase();
    if (ISO_CURRENCY_RE.test(u)) return u;
  }
  return null;
}

/**
 * @param {unknown} raw
 * @returns {string|null}
 */
export function formatHotelProfileSupplierRawAmount(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 6,
      useGrouping: true,
    }).format(n);
  } catch {
    return String(n);
  }
}

/**
 * מטבע תצוגה מההקשר (תוצאות חיפוש / בחירת UI) — לא מטבע סכום ה-API.
 * סדר: hotelSearchListDisplayCurrency → selectedHotelCurrency → searchPhrase.currency
 */
export function resolveHotelProfileUiDisplayCurrency(profilePriceContext) {
  if (!profilePriceContext || typeof profilePriceContext !== "object") return null;
  const ordered = [
    profilePriceContext.hotelSearchListDisplayCurrency,
    profilePriceContext.selectedHotelCurrency,
    profilePriceContext.searchPhrase?.currency,
  ];
  for (const raw of ordered) {
    if (raw == null || raw === "") continue;
    const c = normalizeHotelSearchCurrencyCode(raw);
    if (isKnownHotelSearchCurrencyCode(c)) return c;
  }
  return null;
}

/**
 * מטבע גולמי לשורת חבילה: שדות החבילה → מטבע מלון בפרופיל → USD (ברירת מחדל).
 * לא משתמש במטבע חיפוש/תצוגה — כדי שלא יסומן ILS על סכום שסופק ב-USD.
 * @param {object|null|undefined} _profilePriceContext — נשמר לתאימות קריאות קיימות; לא בשימוש
 */
export function resolveHotelProfilePackageRawCurrency(
  pkg,
  hotelInfo,
  _profilePriceContext = null
) {
  const fromPkg = pkg ? getHotelProfilePackageCurrency(pkg) : null;
  if (fromPkg) return fromPkg;
  const fromHotel = getHotelProfileHeroDisplayCurrency(hotelInfo);
  if (fromHotel) return fromHotel;
  return "USD";
}

/**
 * @deprecated השתמש ב-resolveHotelProfileUiDisplayCurrency או העבר אובייקט profilePriceContext מלא.
 * תאימות לאחור: ארגומנטים שטוחים ממיפוי RoomPlacer ישנים.
 */
export function resolveHotelProfileRoomDisplayCurrency(args) {
  if (args && typeof args === "object" && args.requestCurrency !== undefined) {
    return resolveHotelProfileUiDisplayCurrency({
      hotelSearchListDisplayCurrency: args.hotelSearchListDisplayCurrency,
      selectedHotelCurrency: args.requestCurrency,
      searchPhrase: args.searchPhrase ?? null,
    });
  }
  return resolveHotelProfileUiDisplayCurrency(args);
}

/**
 * SoT אחיד: סכום גולמי + מטבע מקור מפורש → מטבע תצוגה + המרה USD↔ILS כשצריך.
 * רישום audit (kind hotel) כשההקלטה פעילה — מטא מ־`pricingAuditMeta` או מ־`profilePriceContext.hotelProfilePricingAuditMeta`.
 *
 * @param {object} p
 * @param {number|null|undefined} p.rawAmount
 * @param {string|null|undefined} p.rawCurrency — ISO; אם חסר — לא מנחשים מטבע לסכום
 * @param {string|null|undefined} p.displayCurrency — יעד תצוגה (USD/ILS)
 * @param {(n: number, fixed?: boolean) => number} [p.usdToIls]
 * @param {(n: number, fixed?: boolean) => number} [p.ilsToUsd]
 * @param {object|null} [p.profilePriceContext] — אם קיים, נלקח ממנו hotelProfilePricingAuditMeta ל-audit
 */
export function resolveProfileDisplayPrice(p) {
  const {
    rawAmount,
    rawCurrency,
    displayCurrency,
    usdToIls,
    ilsToUsd,
    component = "unknown",
    hotelId = null,
    roomKey = null,
    packageId = null,
    sourcePath = "",
    pricingAuditMeta: explicitPricingAuditMeta,
    profilePriceContext = null,
  } = p || {};
  const pricingAuditMetaFromContext =
    profilePriceContext &&
    typeof profilePriceContext === "object" &&
    profilePriceContext.hotelProfilePricingAuditMeta &&
    typeof profilePriceContext.hotelProfilePricingAuditMeta === "object"
      ? profilePriceContext.hotelProfilePricingAuditMeta
      : null;
  const pricingAuditMetaBase =
    explicitPricingAuditMeta && typeof explicitPricingAuditMeta === "object"
      ? explicitPricingAuditMeta
      : pricingAuditMetaFromContext;

  const finishProfileDisplayAudit = (out) => {
    recordPricingAudit({
      kind: "hotel",
      meta: compactPricingAuditMeta({
        ...(pricingAuditMetaBase && typeof pricingAuditMetaBase === "object"
          ? pricingAuditMetaBase
          : {}),
        productType:
          normalizePricingAuditProductType(pricingAuditMetaBase?.productType) ??
          PRICING_AUDIT_PRODUCT_TYPE.HOTEL,
        pricingModel: `resolveProfileDisplayPrice:${component}`,
      }),
      input: {
        rawAmount,
        rawCurrency,
        displayCurrency,
        component,
        hotelId,
        roomKey,
        packageId,
        sourcePath,
      },
      result: out,
    });
    return out;
  };

  const auditSteps = ["profile_display:enter"];
  const n = Number(rawAmount);
  const base = {
    component,
    hotelId,
    roomKey,
    packageId,
    rawAmount: n,
    rawCurrency: null,
    displayCurrency: null,
    convertedAmount: null,
    formattedAmount: null,
    sourcePath,
    conversionApplied: false,
    rawCurrencyMissing: false,
    fxUnavailable: false,
    pricingStatus: "ok",
    auditSteps,
  };
  if (!Number.isFinite(n) || n <= 0) {
    auditSteps.push("reject:invalid_amount");
    return finishProfileDisplayAudit({
      ...base,
      formattedAmount: null,
      pricingStatus: "missing_price",
      auditSteps,
    });
  }

  let rawCur =
    rawCurrency != null && String(rawCurrency).trim() !== ""
      ? String(rawCurrency).trim().toUpperCase()
      : null;
  if (rawCur && !isKnownHotelSearchCurrencyCode(rawCur)) {
    rawCur = null;
  }

  const dispRaw =
    displayCurrency != null && String(displayCurrency).trim() !== ""
      ? normalizeHotelSearchCurrencyCode(displayCurrency)
      : null;
  const disp = dispRaw && isKnownHotelSearchCurrencyCode(dispRaw) ? dispRaw : null;

  if (!rawCur) {
    auditSteps.push("reject:missing_raw_currency_iso");
    const out = {
      ...base,
      rawCurrency: null,
      displayCurrency: disp,
      convertedAmount: null,
      formattedAmount: null,
      rawCurrencyMissing: true,
      conversionApplied: false,
      pricingStatus: "missing_price",
      auditSteps,
    };
    logProfilePriceAudit(out);
    return finishProfileDisplayAudit(out);
  }

  if (!disp) {
    auditSteps.push("display:target_unset:show_in_raw_currency");
    const rounded = roundMoney2(n) ?? n;
    const formattedAmount = formatHotelSearchAmountForDisplay(rounded, rawCur);
    const out = {
      ...base,
      rawCurrency: rawCur,
      displayCurrency: null,
      convertedAmount: rounded,
      formattedAmount,
      conversionApplied: false,
      pricingStatus: "ok",
      auditSteps,
    };
    logProfilePriceAudit(out);
    return finishProfileDisplayAudit(out);
  }

  if (rawCur === disp) {
    auditSteps.push("fx:no_cross_currency");
    const rounded = roundMoney2(n) ?? n;
    const formattedAmount = formatHotelSearchAmountForDisplay(rounded, disp);
    const out = {
      ...base,
      rawCurrency: rawCur,
      displayCurrency: disp,
      convertedAmount: rounded,
      formattedAmount,
      conversionApplied: false,
      pricingStatus: "ok",
      auditSteps,
    };
    logProfilePriceAudit(out);
    return finishProfileDisplayAudit(out);
  }

  auditSteps.push(`fx:cross:${rawCur}->${disp}`);
  const fx = applyFXSettlement({
    amount: n,
    fromCurrency: rawCur,
    toCurrency: disp,
    usdToIls,
    ilsToUsd,
  });

  if (fx.convertedAmount == null) {
    auditSteps.push(`reject:fx:${fx.reason ?? "unknown"}`);
    const out = {
      ...base,
      rawCurrency: rawCur,
      displayCurrency: disp,
      convertedAmount: null,
      formattedAmount: null,
      conversionApplied: false,
      fxUnavailable: true,
      pricingStatus: "missing_price",
      auditSteps,
    };
    logProfilePriceAudit(out);
    return finishProfileDisplayAudit(out);
  }

  const roundedConv = fx.convertedAmount;
  const conversionApplied = fx.fxApplied;
  const formattedAmount = formatHotelSearchAmountForDisplay(roundedConv, disp);
  auditSteps.push("profile_display:ok");
  const out = {
    ...base,
    rawCurrency: rawCur,
    displayCurrency: disp,
    convertedAmount: roundedConv,
    formattedAmount,
    conversionApplied,
    pricingStatus: "ok",
    auditSteps,
  };
  logProfilePriceAudit(out);
  return finishProfileDisplayAudit(out);
}

function logProfilePriceAudit(row) {
  if (!isHotelProfilePriceAuditEnabled()) return;
  try {
    if (typeof console !== "undefined" && console.info) {
      console.info(
        "[PRICE_AUDIT_PROFILE]",
        JSON.stringify({
          component: row.component,
          hotelId: row.hotelId,
          roomKey: row.roomKey,
          packageId: row.packageId,
          rawAmount: row.rawAmount,
          rawCurrency: row.rawCurrency,
          displayCurrency: row.displayCurrency,
          convertedAmount: row.convertedAmount,
          formattedAmount: row.formattedAmount,
          sourcePath: row.sourcePath,
          conversionApplied: row.conversionApplied,
          rawCurrencyMissing: row.rawCurrencyMissing,
          fxUnavailable: row.fxUnavailable,
          pricingStatus: row.pricingStatus,
          auditSteps: row.auditSteps,
        })
      );
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * תצוגת מחיר חבילה/חדר בפרופיל — מטבע מקור מה-API, תצוגה + המרה לפי בחירת חיפוש.
 *
 * @param {object|null|undefined} pkg
 * @param {{ selectedHotelCurrency?: string|null, searchPhrase?: object|null, hotelInfo?: object|null, hotelSearchListDisplayCurrency?: string|null, usdToIls?: function, ilsToUsd?: function, hotelId?: string|number|null }|null|undefined} [profilePriceContext]
 */
export function formatHotelProfilePackagePriceDisplay(pkg, profilePriceContext) {
  const amt = getHotelProfilePackageSupplierUsd(pkg);
  if (amt == null) return null;
  const hotelInfo = profilePriceContext?.hotelInfo ?? null;
  const rawCur = resolveHotelProfilePackageRawCurrency(
    pkg,
    hotelInfo,
    profilePriceContext
  );
  const disp = resolveHotelProfileUiDisplayCurrency(profilePriceContext);
  const r = resolveProfileDisplayPrice({
    rawAmount: amt,
    rawCurrency: rawCur,
    displayCurrency: disp,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    component: "formatHotelProfilePackagePriceDisplay",
    hotelId: profilePriceContext?.hotelId ?? null,
    packageId: pkg?.PackageId ?? pkg?.packageID ?? null,
    sourcePath: "getHotelProfilePackageSupplierUsd",
    profilePriceContext,
  });
  if (r.formattedAmount == null && amt != null) {
    if (import.meta.env.MODE !== "test") {
      console.log("PROFILE PRICE DEBUG", {
        finalPrice: pkg?.finalPrice,
        rawCurrency: rawCur,
        pricingStatus: r.pricingStatus,
        rawCurrencyMissing: r.rawCurrencyMissing,
        fxUnavailable: r.fxUnavailable,
      });
    }
  }
  return r.formattedAmount;
}

export function getHotelProfileHeroDisplayCurrency(hotelInfo) {
  if (!hotelInfo || typeof hotelInfo !== "object") return null;
  const c =
    hotelInfo.Currency ??
    hotelInfo.currency ??
    hotelInfo.PriceCurrency ??
    hotelInfo.priceCurrency;
  if (c == null || c === "") return null;
  const u = String(c).trim().toUpperCase();
  return ISO_CURRENCY_RE.test(u) ? u : null;
}

/**
 * Loaded profile hero: StartingPrice / min package — מטבע מקור מעסקה, המרה לתצוגה.
 */
export function formatHotelProfileHeroLoadedPriceDisplay(hotelInfo, profilePriceContext) {
  const pick = pickProfileLoadedHeroUsd(hotelInfo);
  if (pick.supplierUsd == null) return { display: null, pick };
  const rawCur = pickProfileLoadedHeroTransactionCurrency(hotelInfo, pick);
  if (profilePriceContext?.dynamicPackageProfileForceIls) {
    if (rawCur == null || String(rawCur).trim() === "") {
      return { display: null, pick };
    }
    const fx = applyFXSettlement({
      amount: pick.supplierUsd,
      fromCurrency: rawCur,
      toCurrency: "ILS",
      usdToIls: profilePriceContext?.usdToIls,
      ilsToUsd: profilePriceContext?.ilsToUsd,
    });
    return {
      display:
        fx.convertedAmount != null
          ? formatHotelIlsRoundedDisplay(fx.convertedAmount)
          : null,
      pick,
    };
  }
  const disp = resolveHotelProfileUiDisplayCurrency(profilePriceContext);
  const r = resolveProfileDisplayPrice({
    rawAmount: pick.supplierUsd,
    rawCurrency: rawCur,
    displayCurrency: disp,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    component: "formatHotelProfileHeroLoadedPriceDisplay",
    hotelId:
      hotelInfo?.HotelId ??
      hotelInfo?.hotelId ??
      profilePriceContext?.hotelId ??
      null,
    sourcePath: pick.priceSource ?? "pickProfileLoadedHeroUsd",
    profilePriceContext,
  });
  return {
    display: r.formattedAmount,
    pick,
  };
}

/**
 * Shell / seed hero before hotelInformation (navigation composite or search seed).
 */
export function formatHotelProfileShellSeedPriceDisplay({
  compositePricingMeta,
  seed,
  profilePriceContext,
}) {
  const shellPick = pickProfileShellSupplierUsd({ compositePricingMeta, seed });
  if (shellPick.supplierUsd == null) {
    return { display: null, shellPick };
  }
  const rawCur = pickProfileShellTransactionCurrency({
    compositePricingMeta,
    seed,
    profilePriceContext,
  });
  const disp = resolveHotelProfileUiDisplayCurrency(profilePriceContext);
  const r = resolveProfileDisplayPrice({
    rawAmount: shellPick.supplierUsd,
    rawCurrency: rawCur,
    displayCurrency: disp,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    component: "formatHotelProfileShellSeedPriceDisplay",
    hotelId: profilePriceContext?.hotelId ?? seed?.id ?? null,
    sourcePath: shellPick.priceSource ?? "pickProfileShellSupplierUsd",
    profilePriceContext,
  });
  return {
    display: r.formattedAmount,
    shellPick,
  };
}

/**
 * Minimal ourPrice for OrderContext merge when entering offer flow — raw API amount only (no FX/commission here).
 * @param {object|null|undefined} pkg
 * @returns {object|null}
 */
export function buildHotelOrderOurPriceFromPackageRaw(pkg) {
  const raw = getHotelProfilePackageSupplierUsd(pkg);
  if (raw == null || !Number.isFinite(Number(raw)) || Number(raw) <= 0) return null;
  const n = Number(raw);
  const cur = getHotelProfilePackageCurrency(pkg);
  return {
    initialPrice: n,
    priceWithCommission: n,
    hrCommission: 0,
    agentCommission: 0,
    creditCardFee: 0,
    pricePreFinalRound: n,
    priceWithCommissionAndCreditCardFee: n,
    ...(cur ? { originalCurrency: cur } : {}),
  };
}

function cloneHotelPackageOurPriceObject(ourPrice) {
  if (!ourPrice || typeof ourPrice !== "object") return null;
  try {
    if (typeof structuredClone === "function") {
      return structuredClone(ourPrice);
    }
  } catch {
    /* fallthrough */
  }
  try {
    return JSON.parse(JSON.stringify(ourPrice));
  } catch {
    return { ...ourPrice };
  }
}

/**
 * טיוטת הזמנה: עדיף ourPrice עשיר מהחבילה (HR / VM); רק אם חסר — מינימום supplier USD.
 * @param {object|null|undefined} pkg
 * @returns {object|null}
 */
export function pickHotelPackageOrderOurPrice(pkg) {
  if (pkg?.ourPrice && typeof pkg.ourPrice === "object") {
    const cloned = cloneHotelPackageOurPriceObject(pkg.ourPrice);
    if (cloned) return cloned;
  }
  return buildHotelOrderOurPriceFromPackageRaw(pkg);
}

/**
 * Single selling display: base in supplier USD → ILS → אותו מסלול עמלות כמו `calcBaseCommission` (דרך pricingEngine).
 * דורש `commissionContext` תואם ל־`buildAppCommissionContext` (מ־AppContext).
 */
export function resolveHotelSellingDisplay({
  supplierUsd,
  usdToIls,
  linkedFlightsDealPrice = 0,
  ilsToUsd,
  commissionContext,
  context = "unknown",
  priceSource = null,
  fallbackUsed = false,
  pricingAuditMeta,
}) {
  const r = runHotelSellingDisplayFromEngine({
    supplierUsd,
    usdToIls,
    ilsToUsd,
    linkedFlightsDealPrice,
    commissionContext,
    context,
    priceSource,
    fallbackUsed,
  });
  const out = {
    sourcePrice: r.sourcePrice,
    sourceCurrency: r.sourceCurrency,
    finalDisplayPrice: r.finalDisplayPrice,
    finalDisplayPriceUsd: r.finalDisplayPriceUsd,
    commissionApplied: r.commissionApplied,
    labelKey: r.labelKey,
    metadata: r.metadata,
    pricingStatus: r.pricingStatus,
    auditSteps: r.auditSteps,
  };

  recordPricingAudit({
    kind: "hotel",
    meta: compactPricingAuditMeta({
      ...(pricingAuditMeta && typeof pricingAuditMeta === "object"
        ? pricingAuditMeta
        : {}),
      productType:
        normalizePricingAuditProductType(
          pricingAuditMeta && typeof pricingAuditMeta === "object"
            ? pricingAuditMeta.productType
            : undefined
        ) ?? PRICING_AUDIT_PRODUCT_TYPE.HOTEL,
      pricingModel: "resolveHotelSellingDisplay",
    }),
    input: {
      supplierUsd,
      linkedFlightsDealPrice,
      commissionContext,
      context,
      priceSource,
      fallbackUsed,
      pricingAuditMeta:
        pricingAuditMeta && typeof pricingAuditMeta === "object"
          ? pricingAuditMeta
          : undefined,
    },
    result: out,
  });

  return out;
}

/** Profile navigation seed: raw search-row `LowestPackagePrice` when present (API units). */
export function hotelProfileSeedSupplierUsd(navItem) {
  return agentHotelSearchLowestPackagePrice(navItem);
}

export function logHotelPricingDebug(tag, payload) {
  try {
    if (typeof console === "undefined" || !console.info) return;
    console.info(`[HOTEL_PRICING_DEBUG] ${tag}`, payload);
  } catch (_) {
    /* ignore */
  }
}
